import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp, Equal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useFormat } from '../format';

type AmountSize = 'display' | 'xl' | 'lg' | 'md' | 'sm';
type Tone = 'default' | 'muted' | 'up' | 'down';

/**
 * Digits roll to their new value like a split-flap board instead of
 * jumping. Place values are keyed from the right so "$980" -> "$1,020"
 * rolls the units/tens/hundreds and slides the new thousands digit in.
 * Screen readers get the plain string; the rolling glyphs are hidden.
 */
export function RollingText({ text }: { text: string }) {
  const chars = [...text];
  return (
    <>
      <span className="roll" aria-hidden="true">
        {chars.map((ch, i) => {
          const place = chars.length - i;
          if (!/\d/.test(ch)) {
            return (
              <span key={`c${place}`} className="roll-char">
                {ch}
              </span>
            );
          }
          return (
            <span key={`d${place}`} className="roll-digit">
              <span className="roll-strip" style={{ transform: `translateY(${-Number(ch) * 10}%)` }}>
                {DIGITS.map((d) => (
                  <span key={d}>{d}</span>
                ))}
              </span>
            </span>
          );
        })}
      </span>
      <span className="visually-hidden">{text}</span>
    </>
  );
}

const DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

export interface AmountProps {
  minor: number;
  currency?: string;
  size?: AmountSize;
  tone?: Tone;
  whole?: boolean;
  signed?: boolean;
  /** Animate digit changes (headline totals). */
  roll?: boolean;
  className?: string;
}

export function Amount({ minor, currency, size = 'md', tone = 'default', whole, signed, roll, className }: AmountProps) {
  const { money } = useFormat();
  const text = money(minor, { currency, whole, signed });
  return (
    <span className={['amount', `amount-${size}`, `tone-${tone}`, 'num', className].filter(Boolean).join(' ')}>
      {roll ? <RollingText text={text} /> : text}
    </span>
  );
}

/**
 * Change vs a previous period. For spending, up is bad (red) and down is
 * good (green); direction is always carried by the arrow and the words,
 * never by color alone.
 */
export function Delta({
  ratio,
  goodWhen = 'down',
  size = 'md',
  showValue = true,
}: {
  ratio: number | null;
  goodWhen?: 'down' | 'up';
  size?: 'md' | 'lg';
  showValue?: boolean;
}) {
  const { t } = useTranslation();
  const { percent } = useFormat();
  if (ratio === null) return <span className="delta tone-muted">—</span>;
  // Direction follows the figure shown (whole percents): −0.4 % reads "= 0%", never "↓ 0%".
  const rounded = Math.round(ratio * 100) / 100;
  const dir = rounded > 0 ? 'up' : rounded < 0 ? 'down' : 'flat';
  // Row-level changes read as whole percents ("24%"); the headline keeps one decimal.
  const value = percent(Math.abs(rounded), { signed: false, digits: 0 });
  const tone = dir === 'flat' ? 'muted' : (dir === 'up') === (goodWhen === 'up') ? 'down' : 'up';
  const Icon = dir === 'up' ? ArrowUp : dir === 'down' ? ArrowDown : Equal;
  const label = dir === 'flat' ? t('delta.flat') : t(`delta.${dir}`, { value });
  return (
    <span className={`delta delta-${size} tone-${tone} num`} aria-label={label} role="img">
      <Icon aria-hidden size={size === 'lg' ? 18 : 16} strokeWidth={2.25} />
      {showValue && <span>{value}</span>}
    </span>
  );
}

/** Signed percentage in running text, e.g. "+8.4%" next to the month's total. */
export function Change({ ratio }: { ratio: number }) {
  const { percent } = useFormat();
  const tone = ratio > 0 ? 'up' : ratio < 0 ? 'down' : 'muted';
  return <span className={`change tone-${tone} num`}>{percent(ratio)}</span>;
}

/**
 * The headline block: what was spent, how it moved, how much budget is
 * left. Numbers first; words are supplied by the screen so they localize.
 */
export function TotalSummary({
  label,
  minor,
  change,
  meter,
  caption,
}: {
  label: string;
  minor: number;
  change?: ReactNode;
  meter?: ReactNode;
  caption?: ReactNode;
}) {
  return (
    <section className="total" aria-label={label}>
      <p className="total-label">{label}</p>
      <div className="total-row">
        <Amount minor={minor} size="display" whole roll />
        {change && <div className="total-change">{change}</div>}
      </div>
      {meter && <div className="total-meter">{meter}</div>}
      {caption && <p className="total-caption num">{caption}</p>}
    </section>
  );
}

export function CategoryDot({ color, size = 12 }: { color: string; size?: number }) {
  return <span className="dot" style={{ background: color, width: size, height: size }} aria-hidden="true" />;
}

/** Household member initials. Neutral on purpose: members are not ranked or color-coded. */
export function Avatar({ name, size = 28 }: { name: string; size?: number }) {
  const initials = name
    .split(/\s+/)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return (
    <span className="avatar" style={{ width: size, height: size, fontSize: size * 0.4 }} title={name} aria-label={name} role="img">
      {initials}
    </span>
  );
}

export function AvatarStack({ names }: { names: string[] }) {
  return (
    <span className="avatar-stack">
      {names.map((n) => (
        <Avatar key={n} name={n} size={24} />
      ))}
    </span>
  );
}

export function Wordmark({ name = 'COSTLINE' }: { name?: string }) {
  return <span className="wordmark">{name}</span>;
}

export function ScreenTitle({ children }: { children: ReactNode }) {
  return <h1 className="screen-title">{children}</h1>;
}

export function SectionHeading({ children, action, id }: { children: ReactNode; action?: ReactNode; id?: string }) {
  return (
    <div className="section-heading">
      <h2 id={id}>{children}</h2>
      {action}
    </div>
  );
}
